"use client";

import { motion } from "framer-motion";
import { forwardRef } from "react";

const PremiumButton = forwardRef(function PremiumButton({
  children,
  size = "md",
  variant = "primary",
  href,
  onClick,
  disabled = false,
  className = "",
  type = "button",
  as = "auto", // "auto", "button", "a", "motion.a", "motion.button"
  gaEvent = null, // GA4 event tracking
  ...props
}, ref) {
  
  // Size variants
  const sizeClasses = {
    sm: "px-4 py-2 text-sm",
    md: "px-6 py-3 text-base",
    lg: "px-8 py-4 text-lg",
    xl: "px-10 py-5 text-xl"
  };

  // Variant styles
  const variantClasses = {
    primary: "bg-[var(--brand-color)] text-[var(--bg-primary)] hover:bg-[var(--brand-hover)]",
    secondary: "bg-transparent text-[var(--text-primary)] border border-[var(--border-light)] hover:border-[var(--brand-color)] hover:text-[var(--brand-color)]"
  };

  // Base classes
  const baseClasses = "glow-button inline-flex items-center justify-center gap-2 font-semibold rounded-sm transition-all duration-300 relative overflow-hidden";

  // Hover effects for primary variant
  const hoverClasses = variant === "primary"
    ? "hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[var(--brand-color)]/25"
    : "";

  // Disabled classes
  const disabledClasses = disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer";

  // Combine all classes
  const combinedClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${hoverClasses} ${disabledClasses} ${className}`.trim();

  // GA4 tracking handler
  const handleClick = (e) => {
    // Track GA4 event if provided
    if (gaEvent && typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', gaEvent.action, {
        event_category: gaEvent.category || 'CTA',
        event_label: gaEvent.label || 'Button Click',
        page_location: window.location.href,
        page_title: document.title,
        ...gaEvent.parameters
      });
    }
    
    // Call original onClick handler
    if (onClick && !disabled) {
      onClick(e);
    }
  };

  // Auto-detect component type
  let Component = "button";
  if (as !== "auto") {
    Component = as;
  } else if (href) {
    Component = "a";
  }

  // Motion variants for animations
  const motionProps = {
    whileHover: disabled ? {} : { scale: 1.02, y: -2 },
    whileTap: disabled ? {} : { scale: 0.98 },
    transition: { type: "spring", stiffness: 300 }
  };

  // Handle motion components
  if (Component === "motion.a" || Component === "motion.button") {
    const MotionComponent = Component === "motion.a" ? motion.a : motion.button;
    
    return (
      <MotionComponent
        ref={ref}
        href={href}
        onClick={disabled ? undefined : handleClick}
        disabled={disabled}
        type={href ? undefined : type}
        className={combinedClasses}
        {...motionProps}
        {...props}
      >
        {children}
      </MotionComponent>
    );
  }

  // Handle regular components
  if (Component === "a") {
    return (
      <a
        ref={ref}
        href={href}
        onClick={disabled ? undefined : handleClick}
        className={combinedClasses}
        {...props}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      ref={ref}
      onClick={disabled ? undefined : handleClick}
      disabled={disabled}
      type={type}
      className={combinedClasses}
      {...props}
    >
      {children}
    </button>
  );
});

export default PremiumButton;